/**
 * Shared geometry operations for heavy computational tasks
 * Used by both workers and main thread fallback
 */
import * as THREE from 'three';

// Deformation modes
export const DEFORMATION_MODE = {
  UNIFORM: 'uniform',
  GRADIENT: 'gradient'
};

// Constants
const MIN_FALLOFF_RADIUS = 0.5;
const MAX_FALLOFF_RADIUS = 5.0;
const DEFAULT_FALLOFF_MULTIPLIER = 2.5;

/**
 * Calculate the average edge length of a mesh for determining falloff radius
 */
export function calculateAverageEdgeLength(positions, indices) {
  if (!indices || indices.length === 0) {
    console.warn('Geometry must be indexed for edge length calculation');
    return 1.0;
  }

  const vertex = new THREE.Vector3();
  const vertexB = new THREE.Vector3();

  let totalLength = 0;
  let edgeCount = 0;
  const processedEdges = new Set();

  // Process each face and its edges
  for (let i = 0; i < indices.length; i += 3) {
    const a = indices[i];
    const b = indices[i + 1];
    const c = indices[i + 2];

    // Check each edge of the triangle
    const edges = [
      [Math.min(a, b), Math.max(a, b)],
      [Math.min(b, c), Math.max(b, c)],
      [Math.min(c, a), Math.max(c, a)]
    ];

    for (const [v1, v2] of edges) {
      const edgeKey = `${v1}-${v2}`;
      if (!processedEdges.has(edgeKey)) {
        processedEdges.add(edgeKey);

        vertex.set(positions[v1 * 3], positions[v1 * 3 + 1], positions[v1 * 3 + 2]);
        vertexB.set(positions[v2 * 3], positions[v2 * 3 + 1], positions[v2 * 3 + 2]);

        const length = vertex.distanceTo(vertexB);
        totalLength += length;
        edgeCount++;
      }
    }
  }

  return edgeCount > 0 ? totalLength / edgeCount : 1.0;
}

/**
 * Calculate distance from a point to the closest point on a curve
 */
export function distanceToClosestCurvePoint(point, curvePoints) {
  let minDistance = Infinity;

  for (let i = 0; i < curvePoints.length; i += 3) {
    const dx = point.x - curvePoints[i];
    const dy = point.y - curvePoints[i + 1];
    const dz = point.z - curvePoints[i + 2];
    const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

    if (distance < minDistance) {
      minDistance = distance;
    }
  }

  return minDistance;
}

/**
 * Check if point is inside polygon using ray casting (XZ plane projection)
 */
export function isPointInPolygonXZ(point, polygonPoints) {
  let inside = false;
  const x = point.x, z = point.z;

  for (let i = 0, j = polygonPoints.length - 1; i < polygonPoints.length; j = i++) {
    const xi = polygonPoints[i].x, zi = polygonPoints[i].z;
    const xj = polygonPoints[j].x, zj = polygonPoints[j].z;

    if (((zi > z) !== (zj > z)) && (x < (xj - xi) * (z - zi) / (zj - zi) + xi)) {
      inside = !inside;
    }
  }

  return inside;
}

/**
 * Get index of closest curve point to a vertex
 */
export function getClosestIndex3D(point, curvePoints) {
  let minDistSq = Infinity;
  let closestIdx = 0;

  for (let i = 0; i < curvePoints.length; i += 3) {
    const dx = point.x - curvePoints[i];
    const dy = point.y - curvePoints[i + 1];
    const dz = point.z - curvePoints[i + 2];
    const distSq = dx * dx + dy * dy + dz * dz;

    if (distSq < minDistSq) {
      minDistSq = distSq;
      closestIdx = Math.floor(i / 3);
    }
  }

  return closestIdx;
}

/**
 * Determine if curve normals should be reversed using center point method
 */
export function shouldReverseCurveNormals(curvePoints, curveNormals) {
  if (!curvePoints || !curveNormals || curvePoints.length < 9 || curveNormals.length < 3) {
    return false;
  }

  // Calculate center point
  const center = new THREE.Vector3(0, 0, 0);
  const pointCount = curvePoints.length / 3;

  for (let i = 0; i < curvePoints.length; i += 3) {
    center.x += curvePoints[i];
    center.y += curvePoints[i + 1];
    center.z += curvePoints[i + 2];
  }
  center.divideScalar(pointCount);

  // Check if normals point toward or away from center
  let inwardCount = 0;
  let outwardCount = 0;

  for (let i = 0; i < pointCount; i++) {
    const pointX = curvePoints[i * 3];
    const pointY = curvePoints[i * 3 + 1];
    const pointZ = curvePoints[i * 3 + 2];

    const toCenterX = center.x - pointX;
    const toCenterY = center.y - pointY;
    const toCenterZ = center.z - pointZ;
    const length = Math.sqrt(toCenterX * toCenterX + toCenterY * toCenterY + toCenterZ * toCenterZ);

    if (length > 1e-6) {
      const normalizedX = toCenterX / length;
      const normalizedY = toCenterY / length;
      const normalizedZ = toCenterZ / length;

      const normal = curveNormals[i];
      const dot = normal.x * normalizedX + normal.y * normalizedY + normal.z * normalizedZ;

      if (dot > 0) inwardCount++;
      else if (dot < 0) outwardCount++;
    }
  }

  return outwardCount > inwardCount;
}

/**
 * Check if vertex is inside curve region using curve normals
 * @param {THREE.Vector3} vertex - The vertex to check
 * @param {Float32Array} curvePoints - Flat array of curve point coordinates (x,y,z,x,y,z,...)
 * @param {Array} curveNormals - Array of curve normals (objects with x,y,z)
 * @param {Array} curveSurfaceNormals - Array of surface normals at curve points (for back-face rejection)
 * @param {THREE.Vector3} vertexNormal - Surface normal at the vertex position (for back-face rejection)
 * @returns {boolean} - True if vertex is inside the curve region
 */
export function isVertexInsideCurveRegion(vertex, curvePoints, curveNormals = null, curveSurfaceNormals = null, vertexNormal = null) {
  if (!curvePoints || curvePoints.length < 6) return false;

  // Convert flat array to Vector3 array for polygon test
  const points = [];
  for (let i = 0; i < curvePoints.length; i += 3) {
    points.push(new THREE.Vector3(curvePoints[i], curvePoints[i + 1], curvePoints[i + 2]));
  }

  // Get the closest curve point index for all subsequent checks
  const idx = getClosestIndex3D(vertex, curvePoints);

  // **CRITICAL FIX: Back-face rejection - check if vertex is on the same side of mesh as the curve**
  // This prevents vertices on the opposite side of tubular meshes from being selected
  if (curveSurfaceNormals && vertexNormal && curveSurfaceNormals[idx]) {
    const curveSurfaceNormal = curveSurfaceNormals[idx];
    
    // Calculate dot product between vertex normal and curve surface normal
    // If they point in opposite directions (dot < 0), vertex is on the back side
    const normalDot = vertexNormal.x * curveSurfaceNormal.x + 
                      vertexNormal.y * curveSurfaceNormal.y + 
                      vertexNormal.z * curveSurfaceNormal.z;
    
    // Threshold for "opposite" normals - if dot < 0.1, normals are pointing away from each other
    // This accounts for some curvature but rejects clear back-facing vertices
    const BACK_FACE_THRESHOLD = 0.1;
    
    if (normalDot < BACK_FACE_THRESHOLD) {
      // Vertex's surface normal points away from curve's surface - it's on the back side
      return false;
    }
  }

  // Use curve normal-based test if available
  if (Array.isArray(curveNormals) && curveNormals.length === points.length) {
    const pc = points[idx];
    const nc = curveNormals[idx];

    if (pc && nc) {
      const vx = vertex.x - pc.x;
      const vy = vertex.y - pc.y;
      const vz = vertex.z - pc.z;
      const dot = nc.x * vx + nc.y * vy + nc.z * vz;

      const shouldReverse = shouldReverseCurveNormals(curvePoints, curveNormals);
      const correctedDot = shouldReverse ? -dot : dot;

      return correctedDot > 0;
    }
  }

  // Fallback to 2D polygon test
  return isPointInPolygonXZ(vertex, points);
}

/**
 * Calculate smooth falloff weight
 */
export function calculateFalloffWeight(distanceFromBoundary, falloffRadius, falloffType = 'smoothstep') {
  if (distanceFromBoundary <= 0) return 1.0;
  if (distanceFromBoundary >= falloffRadius) return 0.0;

  const normalizedDistance = distanceFromBoundary / falloffRadius;

  switch (falloffType) {
    case 'linear':
      return 1.0 - normalizedDistance;

    case 'cubic':
      const t = 1.0 - normalizedDistance;
      return t * t * t;

    case 'smoothstep':
    default:
      const t2 = 1.0 - normalizedDistance;
      return t2 * t2 * (3.0 - 2.0 * t2);
  }
}

/**
 * Apply easing function to normalized value
 */
export function applyEasing(t, easingFunction = 'linear') {
  // Clamp input to [0, 1] range
  t = Math.max(0, Math.min(1, t));

  switch (easingFunction) {
    case 'linear':
      return t;

    case 'ease-in-quad':
    case 'easeInQuad':
      return t * t;

    case 'ease-out-quad':
    case 'easeOutQuad':
      return t * (2 - t);

    case 'ease-in-out-quad':
    case 'easeInOutQuad':
      return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

    case 'ease-in-cubic':
    case 'easeInCubic':
      return t * t * t;

    case 'ease-out-cubic':
    case 'easeOutCubic':
      return (--t) * t * t + 1;

    case 'ease-in-out-cubic':
    case 'easeInOutCubic':
      return t * t * (3 - 2 * t); // Smoothstep

    case 'ease-in-quint':
    case 'easeInQuint':
      return t * t * t * t * t;

    case 'ease-out-quint':
    case 'easeOutQuint':
      return (--t) * t * t * t * t + 1;

    case 'ease-in-out-quint':
    case 'easeInOutQuint':
      return t < 0.5 ? 16 * t * t * t * t * t : 1 + 16 * (--t) * t * t * t * t;

    case 'ease-in-sine':
    case 'easeInSine':
      return 1 - Math.cos((t * Math.PI) / 2);

    case 'ease-out-sine':
    case 'easeOutSine':
      return Math.sin((t * Math.PI) / 2);

    case 'ease-in-out-sine':
    case 'easeInOutSine':
      return -(Math.cos(Math.PI * t) - 1) / 2;

    case 'ease-in-expo':
    case 'easeInExpo':
      return t === 0 ? 0 : Math.pow(2, 10 * t - 10);

    case 'ease-out-expo':
    case 'easeOutExpo':
      return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);

    case 'ease-in-out-expo':
    case 'easeInOutExpo':
      return t === 0 ? 0 : t === 1 ? 1 : t < 0.5
        ? Math.pow(2, 20 * t - 10) / 2
        : (2 - Math.pow(2, -20 * t + 10)) / 2;

    case 'smoothstep':
      return t * t * (3 - 2 * t);

    default:
      // Default to smoothstep (ease-in-out-cubic)
      return t * t * (3 - 2 * t);
  }
}

/**
 * Build adjacency list from mesh indices
 */
export function buildAdjacencyList(indices, vertexCount) {
  const adjacency = new Array(vertexCount);
  for (let i = 0; i < vertexCount; i++) {
    adjacency[i] = new Set();
  }

  for (let i = 0; i < indices.length; i += 3) {
    const a = indices[i];
    const b = indices[i + 1];
    const c = indices[i + 2];

    adjacency[a].add(b);
    adjacency[a].add(c);
    adjacency[b].add(a);
    adjacency[b].add(c);
    adjacency[c].add(a);
    adjacency[c].add(b);
  }

  // Convert Sets to Arrays for easier serialization
  return adjacency.map(set => Array.from(set));
}

/**
 * Build adjacency list only for a sparse set of vertex indices.
 * Returns a sparse array where only relevant indices have neighbor arrays.
 */
export function buildSparseAdjacencyList(indices, vertexData) {
  const needed = new Set();
  for (const vd of vertexData) needed.add(vd.index);

  const adjacency = [];

  for (let i = 0; i < indices.length; i += 3) {
    const a = indices[i], b = indices[i + 1], c = indices[i + 2];
    const hasA = needed.has(a), hasB = needed.has(b), hasC = needed.has(c);
    if (!hasA && !hasB && !hasC) continue;

    if (hasA) { if (!adjacency[a]) adjacency[a] = []; adjacency[a].push(b, c); }
    if (hasB) { if (!adjacency[b]) adjacency[b] = []; adjacency[b].push(a, c); }
    if (hasC) { if (!adjacency[c]) adjacency[c] = []; adjacency[c].push(a, b); }
  }

  // Deduplicate
  for (const vd of vertexData) {
    const idx = vd.index;
    if (adjacency[idx]) adjacency[idx] = [...new Set(adjacency[idx])];
  }

  return adjacency;
}

/**
 * Smooth distance field to eliminate Voronoi artifacts
 */
export function smoothDistanceField(vertexData, adjacency, iterations = 3, strength = 0.5) {
  if (vertexData.length === 0) return vertexData;

  // Build vertex index to data index mapping
  const vertexIndexToDataIndex = new Map();
  for (let i = 0; i < vertexData.length; i++) {
    vertexIndexToDataIndex.set(vertexData[i].index, i);
  }

  // Apply iterative smoothing
  for (let iter = 0; iter < iterations; iter++) {
    const smoothedDistances = new Float32Array(vertexData.length);

    for (let i = 0; i < vertexData.length; i++) {
      const vData = vertexData[i];
      const vertexIndex = vData.index;
      const neighbors = adjacency[vertexIndex];

      if (!neighbors || neighbors.length === 0) {
        smoothedDistances[i] = vData.distanceToBoundary;
        continue;
      }

      // Calculate average distance of neighbors
      let sumDistance = 0;
      let count = 0;

      for (const neighborIndex of neighbors) {
        const dataIndex = vertexIndexToDataIndex.get(neighborIndex);
        if (dataIndex !== undefined) {
          sumDistance += vertexData[dataIndex].distanceToBoundary;
          count++;
        }
      }

      if (count > 0) {
        const avgNeighborDistance = sumDistance / count;
        smoothedDistances[i] = vData.distanceToBoundary * (1 - strength) + avgNeighborDistance * strength;
      } else {
        smoothedDistances[i] = vData.distanceToBoundary;
      }
    }

    // Update for next iteration
    for (let i = 0; i < vertexData.length; i++) {
      vertexData[i].distanceToBoundary = smoothedDistances[i];
    }
  }

  return vertexData;
}

/**
 * Apply Laplacian smoothing to vertex positions
 */
export function applyLaplacianSmoothing(positions, adjacency, vertexIndices, iterations = 1, strength = 0.5) {
  const newPositions = new Float32Array(positions);
  const vertex = new THREE.Vector3();
  const neighbor = new THREE.Vector3();
  const laplacian = new THREE.Vector3();

  for (let iter = 0; iter < iterations; iter++) {
    // Calculate smoothed positions
    for (const vertexIndex of vertexIndices) {
      const neighbors = adjacency[vertexIndex];
      if (!neighbors || neighbors.length === 0) continue;

      // Get original position
      vertex.set(
        positions[vertexIndex * 3],
        positions[vertexIndex * 3 + 1],
        positions[vertexIndex * 3 + 2]
      );

      // Calculate average of neighbors
      laplacian.set(0, 0, 0);
      for (const neighborIndex of neighbors) {
        neighbor.set(
          positions[neighborIndex * 3],
          positions[neighborIndex * 3 + 1],
          positions[neighborIndex * 3 + 2]
        );
        laplacian.add(neighbor);
      }
      laplacian.divideScalar(neighbors.length);

      // Calculate Laplacian vector
      laplacian.sub(vertex);

      // Apply smoothing
      vertex.add(laplacian.multiplyScalar(strength));

      // Store smoothed position
      newPositions[vertexIndex * 3] = vertex.x;
      newPositions[vertexIndex * 3 + 1] = vertex.y;
      newPositions[vertexIndex * 3 + 2] = vertex.z;
    }

    // Update positions for next iteration
    if (iter < iterations - 1) {
      for (const vertexIndex of vertexIndices) {
        positions[vertexIndex * 3] = newPositions[vertexIndex * 3];
        positions[vertexIndex * 3 + 1] = newPositions[vertexIndex * 3 + 1];
        positions[vertexIndex * 3 + 2] = newPositions[vertexIndex * 3 + 2];
      }
    }
  }

  // Apply final positions
  for (const vertexIndex of vertexIndices) {
    positions[vertexIndex * 3] = newPositions[vertexIndex * 3];
    positions[vertexIndex * 3 + 1] = newPositions[vertexIndex * 3 + 1];
    positions[vertexIndex * 3 + 2] = newPositions[vertexIndex * 3 + 2];
  }

  return positions;
}

/**
 * Compute vertex deformation data for carve/relief operations
 * This is the main expensive operation that needs to run in workers
 * @param {Float32Array} positions - Vertex positions (flat array)
 * @param {Uint32Array} indices - Triangle indices
 * @param {Float32Array} matrixArray - World matrix as flat array
 * @param {Float32Array} curvePoints - Curve points (flat array)
 * @param {Array} curveNormals - Curve normals (array of {x,y,z} objects)
 * @param {Array} curveSurfaceNormals - Surface normals at curve points (for back-face rejection)
 * @param {Object} options - Deformation options (deformationMode, easingFunction, falloffRadius)
 * @param {Float32Array} normals - Vertex normals (flat array, for back-face rejection)
 */
export function computeVertexDeformation(
  positions,
  indices,
  matrixArray,
  curvePoints,
  curveNormals,
  curveSurfaceNormals = null,
  options = {},
  normals = null
) {
  const {
    deformationMode = DEFORMATION_MODE.GRADIENT,
    easingFunction = 'linear',
    falloffRadius: inputFalloffRadius = null
  } = options;

  let falloffRadius = inputFalloffRadius;
  const vertexCount = positions.length / 3;
  const curvePointCount = curvePoints.length / 3;

  // Calculate falloff radius if not provided
  if (falloffRadius === null) {
    const avgEdgeLength = calculateAverageEdgeLength(positions, indices);
    falloffRadius = Math.min(MAX_FALLOFF_RADIUS, Math.max(MIN_FALLOFF_RADIUS, avgEdgeLength * DEFAULT_FALLOFF_MULTIPLIER));
  }

  // Build transformation matrix
  const matrix = new THREE.Matrix4().fromArray(matrixArray);
  const normalMatrix = new THREE.Matrix3().getNormalMatrix(matrix);
  const vertex = new THREE.Vector3();
  const vertexNormal = new THREE.Vector3();

  // --- PRE-COMPUTE: curve normal reversal (same for all vertices) ---
  const hasCurveNormals = Array.isArray(curveNormals) && curveNormals.length === curvePointCount;
  const reverseNormals = hasCurveNormals ? shouldReverseCurveNormals(curvePoints, curveNormals) : false;

  // --- PRE-COMPUTE: AABB of curve region for fast rejection ---
  let aabbMinX = Infinity, aabbMinY = Infinity, aabbMinZ = Infinity;
  let aabbMaxX = -Infinity, aabbMaxY = -Infinity, aabbMaxZ = -Infinity;
  for (let i = 0; i < curvePoints.length; i += 3) {
    const cx = curvePoints[i], cy = curvePoints[i + 1], cz = curvePoints[i + 2];
    if (cx < aabbMinX) aabbMinX = cx;
    if (cy < aabbMinY) aabbMinY = cy;
    if (cz < aabbMinZ) aabbMinZ = cz;
    if (cx > aabbMaxX) aabbMaxX = cx;
    if (cy > aabbMaxY) aabbMaxY = cy;
    if (cz > aabbMaxZ) aabbMaxZ = cz;
  }
  const aabbMargin = falloffRadius + 2.0;
  aabbMinX -= aabbMargin; aabbMinY -= aabbMargin; aabbMinZ -= aabbMargin;
  aabbMaxX += aabbMargin; aabbMaxY += aabbMargin; aabbMaxZ += aabbMargin;

  const hasSurfaceNormals = curveSurfaceNormals && normals;
  const BACK_FACE_THRESHOLD = 0.1;

  const affectedVertices = [];
  const deformationWeights = [];

  // GRADIENT MODE: Two-pass distance-to-boundary approach
  if (deformationMode === DEFORMATION_MODE.GRADIENT) {
    const insideVertexData = [];
    let maxDistanceToBoundary = 0;

    for (let i = 0; i < vertexCount; i++) {
      vertex.set(positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2]);
      vertex.applyMatrix4(matrix);

      // AABB pre-filter
      if (vertex.x < aabbMinX || vertex.x > aabbMaxX ||
          vertex.y < aabbMinY || vertex.y > aabbMaxY ||
          vertex.z < aabbMinZ || vertex.z > aabbMaxZ) continue;

      // Back-face rejection (inlined)
      if (hasSurfaceNormals) {
        vertexNormal.set(normals[i * 3], normals[i * 3 + 1], normals[i * 3 + 2]);
        vertexNormal.applyMatrix3(normalMatrix).normalize();
      }

      const result = isVertexInsideCurveRegionFast(
        vertex, curvePoints, curvePointCount, curveNormals, hasCurveNormals, reverseNormals,
        curveSurfaceNormals, hasSurfaceNormals ? vertexNormal : null, BACK_FACE_THRESHOLD
      );

      if (result.inside) {
        const distanceToBoundary = result.closestDist;
        insideVertexData.push({ index: i, distanceToBoundary });

        if (distanceToBoundary > maxDistanceToBoundary) {
          maxDistanceToBoundary = distanceToBoundary;
        }
      }
    }

    // Guard against edge cases
    if (maxDistanceToBoundary < 1e-6) {
      maxDistanceToBoundary = 1.0;
    }

    // Smooth distance field (sparse adjacency — only for affected vertices)
    if (insideVertexData.length > 0) {
      const adjacency = buildSparseAdjacencyList(indices, insideVertexData);
      smoothDistanceField(insideVertexData, adjacency, 2, 0.3);

      maxDistanceToBoundary = 0;
      for (const vData of insideVertexData) {
        if (vData.distanceToBoundary > maxDistanceToBoundary) {
          maxDistanceToBoundary = vData.distanceToBoundary;
        }
      }
    }

    // Pass 2: Calculate normalized weights
    for (const vData of insideVertexData) {
      const normalizedDistance = vData.distanceToBoundary / maxDistanceToBoundary;
      const weight = applyEasing(normalizedDistance, easingFunction);

      if (weight > 1e-6) {
        affectedVertices.push(vData.index);
        deformationWeights.push(weight);
      }
    }

  } else {
    // UNIFORM MODE: Flat displacement inside, falloff outside
    for (let i = 0; i < vertexCount; i++) {
      vertex.set(positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2]);
      vertex.applyMatrix4(matrix);

      // AABB pre-filter
      if (vertex.x < aabbMinX || vertex.x > aabbMaxX ||
          vertex.y < aabbMinY || vertex.y > aabbMaxY ||
          vertex.z < aabbMinZ || vertex.z > aabbMaxZ) continue;

      // Back-face rejection (inlined)
      if (hasSurfaceNormals) {
        vertexNormal.set(normals[i * 3], normals[i * 3 + 1], normals[i * 3 + 2]);
        vertexNormal.applyMatrix3(normalMatrix).normalize();
      }

      const result = isVertexInsideCurveRegionFast(
        vertex, curvePoints, curvePointCount, curveNormals, hasCurveNormals, reverseNormals,
        curveSurfaceNormals, hasSurfaceNormals ? vertexNormal : null, BACK_FACE_THRESHOLD
      );
      let weight = 0.0;

      if (result.inside) {
        weight = 1.0;
      } else {
        const distanceToBoundary = result.closestDist;
        if (distanceToBoundary <= falloffRadius) {
          weight = calculateFalloffWeight(distanceToBoundary, falloffRadius, 'smoothstep');
        }
      }

      if (weight > 1e-6) {
        affectedVertices.push(i);
        deformationWeights.push(weight);
      }
    }
  }

  return {
    vertices: new Uint32Array(affectedVertices),
    weights: new Float32Array(deformationWeights),
    falloffRadius,
    deformationMode,
    easingFunction
  };
}

/**
 * Fast inside-curve check with pre-computed values (no per-call allocations).
 * Returns { inside: boolean, closestDist: number } to avoid redundant closest-point search.
 */
const _fastResult = { inside: false, closestDist: 0 };
function isVertexInsideCurveRegionFast(
  vertex, curvePoints, curvePointCount, curveNormals, hasCurveNormals, reverseNormals,
  curveSurfaceNormals, vertexNormal, backFaceThreshold
) {
  _fastResult.inside = false;
  _fastResult.closestDist = 0;
  if (curvePointCount < 2) return _fastResult;

  // Find closest curve point index (inlined getClosestIndex3D)
  let minDistSq = Infinity;
  let idx = 0;
  for (let i = 0; i < curvePoints.length; i += 3) {
    const dx = vertex.x - curvePoints[i];
    const dy = vertex.y - curvePoints[i + 1];
    const dz = vertex.z - curvePoints[i + 2];
    const distSq = dx * dx + dy * dy + dz * dz;
    if (distSq < minDistSq) {
      minDistSq = distSq;
      idx = (i / 3) | 0;
    }
  }

  _fastResult.closestDist = Math.sqrt(minDistSq);

  // Back-face rejection
  if (curveSurfaceNormals && vertexNormal && curveSurfaceNormals[idx]) {
    const csn = curveSurfaceNormals[idx];
    const normalDot = vertexNormal.x * csn.x + vertexNormal.y * csn.y + vertexNormal.z * csn.z;
    if (normalDot < backFaceThreshold) return _fastResult;
  }

  // Curve normal half-plane test
  if (hasCurveNormals) {
    const nc = curveNormals[idx];
    if (nc) {
      const pcX = curvePoints[idx * 3];
      const pcY = curvePoints[idx * 3 + 1];
      const pcZ = curvePoints[idx * 3 + 2];
      const dot = nc.x * (vertex.x - pcX) + nc.y * (vertex.y - pcY) + nc.z * (vertex.z - pcZ);
      _fastResult.inside = reverseNormals ? (-dot > 0) : (dot > 0);
      return _fastResult;
    }
  }

  // Fallback to 2D polygon test (rare path)
  const points = [];
  for (let i = 0; i < curvePoints.length; i += 3) {
    points.push({ x: curvePoints[i], z: curvePoints[i + 2] });
  }
  _fastResult.inside = isPointInPolygonXZ(vertex, points);
  return _fastResult;
}
